import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * El botón "VER PRODUCTO" que sube desde el borde de la tarjeta.
 *
 * ── No es un enlace ──────────────────────────────────────────────────
 * Es un `span`. El enlace es la tarjeta entera —quien pulsa la foto, el
 * nombre o el precio va al mismo sitio—, y meter aquí un segundo `<a>`
 * dentro del primero es HTML inválido: el navegador cierra el enlace
 * exterior por su cuenta y la mitad de la tarjeta deja de ser pulsable.
 * Este trozo es la SEÑAL de que la tarjeta lleva a alguna parte.
 *
 * ── Por qué `group-focus-within` y no solo `group-hover` ─────────────
 * Un botón que solo existe con el ratón encima no existe para quien
 * navega con el tabulador: la tarjeta recibe el foco y la única pista de
 * que hay algo que pulsar está escondida. `group-focus-within` lo saca
 * con el foco igual que el ratón lo saca al pasar. El `.group` está en el
 * enlace que envuelve la tarjeta, y un elemento con foco satisface
 * `:focus-within` por sí mismo, sin necesidad de hijos enfocables.
 *
 * ── `motion-reduce` ──────────────────────────────────────────────────
 * Quien pidió menos movimiento recibe el botón sin el deslizamiento, pero
 * lo recibe: se apaga la animación, no la información.
 */
export interface ProductCardCtaProps {
  /** Color del botón (tienda personalizada). Sin él, la píldora blanca. */
  accent?: string | undefined;
}

export function ProductCardCta({ accent }: ProductCardCtaProps) {
  const { t } = useTranslation();

  return (
    <div
      className={[
        "mt-4 translate-y-2 opacity-0 transition-all duration-500 ease-out",
        "group-hover:translate-y-0 group-hover:opacity-100",
        "group-focus-within:translate-y-0 group-focus-within:opacity-100",
        "motion-reduce:transition-none motion-reduce:transform-none",
      ].join(" ")}
    >
      <span
        className={
          accent
            ? "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white shadow-lg"
            : "inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ds-ink shadow-lg"
        }
        style={accent ? { background: accent } : undefined}
      >
        {t("Ver producto")}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    </div>
  );
}
